import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ILoginIn } from '@app/core/contracts/dto';
import { AuthService } from '@app/core/services/auth-service';
import { UserService } from '@app/core/services/user-service';
import { LoadingController, NavController, Platform } from '@ionic/angular';
import { Subject } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  private nav = inject(NavController);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private loadingController = inject(LoadingController);
  platform = inject(Platform);
  private activeRoute = inject(ActivatedRoute);
  private userService = inject(UserService);

  componentDestroyed$: Subject<boolean> = new Subject();
  loginError: boolean = false;
  authForm: FormGroup;
  returnUrl: string;
  username: FormControl<string>;
  password: FormControl<string>;

  async ngOnInit() {
    this.initFields();
    this.buildForm();
    this.addValueChanges();
    this.authService.logout();
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/secure/dashboard';
    this.activeRoute.queryParams.subscribe((queryParams) => {
      if (queryParams['returnUrl']) {
        this.returnUrl = queryParams['returnUrl'];
      }
    });
  }

  initFields() {
    this.username = new FormControl('', [Validators.required, Validators.minLength(4)]);
    this.password = new FormControl('', [Validators.required, Validators.minLength(4)]);
  }

  buildForm() {
    this.authForm = new FormGroup({
      username: this.username,
      password: this.password
    });
  }

  addValueChanges(): void {
    this.username.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.loginError = false;
    });
    this.password.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.loginError = false;
    });
  }

  async onSubmit() {
    if (this.authForm.valid) {
      const loading = await this.loadingController.create({
        message: 'Please wait...'
      });

      await loading.present();
      let loginModel = null;

      const loginBody: ILoginIn = {
        ...this.authForm.value,
        platformInformation: this.getPlatformInformation()
      };

      this.authService
        .login(loginBody)
        .pipe(
          map((loginResponse) => {
            loginModel = loginResponse;
          }),
          concatMap(() => this.authService.setUserContext()),
          concatMap(() => this.userService.setUserPatient(this.authService.userContext.patientId))
        )
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: () => {
            loading.dismiss();
            if (loginModel.requiresPasswordChange) {
              this.authService.resetPasswordCode = this.authForm.value.password;
              this.nav.navigateRoot(['/resetpassword']);
            } else if (!this.userService.userPatient.verified) {
              this.nav.navigateRoot('/secure/account');
            } else {
              this.nav.navigateRoot(this.returnUrl);
            }
          },
          error: () => {
            loading.dismiss();
            this.loginError = true;
          }
        });
    }
  }

  getPlatformInformation() {
    const userAgent = navigator.userAgent;

    // Mobile platform detection
    if (/android/i.test(userAgent)) {
      return 'android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window)) {
      return 'ios';
    }

    // Browser detection
    if (/chrome|crios/i.test(userAgent) && !/edge|edg/i.test(userAgent)) {
      return 'chrome';
    }

    if (/firefox|fxios/i.test(userAgent)) {
      return 'firefox';
    }

    if (/safari/i.test(userAgent) && !/chrome|crios|android/i.test(userAgent)) {
      return 'safari';
    }

    if (/edg/i.test(userAgent)) {
      return 'edge';
    }

    if (/opr|opera/i.test(userAgent)) {
      return 'opera';
    }

    return 'unknown';
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
